import type {
  ProposalCollaborationEnvelope,
  ProposalContentOperation,
  ProposalPublicationSelection,
} from "work/proposals/negotiation/proposal-collaboration-contract";
import { getProposalCollectionItemIdentity } from "work/proposals/negotiation/proposal-operation-identity";
import { readProposalOperationValue } from "work/proposals/negotiation/proposal-operation-projector";

type IdFactory = () => string;

/**
 * Builds the semantic inverse edits chosen in the submission confirmation.
 *
 * Submission no longer withholds a private tranche: every remaining unpublished change is published.
 * A leader who does not want to send one must explicitly restore the value the counterparty currently
 * sees. These operations are queued and saved before the lifecycle command, so refresh, realtime, and
 * the command response all converge on the same event-backed workspace.
 */
export function buildProposalSubmissionRevertOperations(args: {
  envelope: ProposalCollaborationEnvelope;
  selections: readonly ProposalPublicationSelection[];
  createId?: IdFactory;
}): ProposalContentOperation[] {
  const { envelope, selections, createId = () => crypto.randomUUID() } = args;

  const operations: ProposalContentOperation[] = [];
  for (const selection of selections) {
    const line = envelope.changeLines.find(
      (candidate) =>
        candidate.lastChangedByParty === envelope.viewingParty &&
        candidate.isPublished === false &&
        candidate.conflictKey === selection.conflictKey &&
        (candidate.itemId ?? undefined) === selection.itemId,
    );
    if (!line) continue;

    const currentValue = readProposalOperationValue(envelope.projection, line.path);
    if (!line.itemId) {
      operations.push({
        operationId: createId(),
        kind: "FieldSet" as const,
        path: line.path,
        conflictKey: line.conflictKey,
        expectedValue: currentValue as never,
        ...(line.previousValueExists ? { value: structuredClone(line.previousValue) as never } : {}),
      });
      continue;
    }

    const collection = Array.isArray(currentValue) ? currentValue : [];
    const currentItem = collection.find(
      (item) => getProposalCollectionItemIdentity(item) === line.itemId?.toLowerCase(),
    );
    if (!line.previousValueExists) {
      if (currentItem === undefined) continue;
      operations.push({
        operationId: createId(),
        kind: "CollectionItemRemoved" as const,
        path: line.path,
        conflictKey: line.conflictKey,
        itemId: line.itemId,
        expectedValue: structuredClone(currentItem) as never,
      });
      continue;
    }

    const previousValue = structuredClone(line.previousValue) as never;
    operations.push(
      currentItem === undefined
        ? {
            operationId: createId(),
            kind: "CollectionItemAdded" as const,
            path: line.path,
            conflictKey: line.conflictKey,
            itemId: line.itemId,
            value: previousValue,
          }
        : {
            operationId: createId(),
            kind: "CollectionItemUpdated" as const,
            path: line.path,
            conflictKey: line.conflictKey,
            itemId: line.itemId,
            expectedValue: structuredClone(currentItem) as never,
            value: previousValue,
          },
    );
  }
  return operations;
}
